import { LogoMark } from "@/components/Logo";
import { useLocation } from "wouter";
import {
  ArrowRight,
  BarChart3,
  Check,
  CheckCircle2,
  Grid3x3,
  Mail,
  MessageCircle,
  Play,
  Send,
  TrendingUp,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";

const WRAP = "max-w-[1240px] mx-auto px-5 md:px-8";

export default function Landing() {
  const [, setLocation] = useLocation();
  const goLogin = () => setLocation("/login");

  return (
    <div
      className="min-h-screen"
      style={{
        background: "var(--brand-bone)",
        color: "var(--brand-ink)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <Nav goLogin={goLogin} />
      <Hero goLogin={goLogin} />
      <Logos />
      <Features />
      <Flow />
      <StatsBand />
      <Testimonial />
      <Pricing goLogin={goLogin} />
      <FAQ />
      <FinalCTA goLogin={goLogin} />
      <Footer />
    </div>
  );
}

// ─── NAV ──────────────────────────────────────────────────────────────

function Nav({ goLogin }: { goLogin: () => void }) {
  return (
    <div
      className="sticky top-0 z-30 backdrop-blur"
      style={{
        background: "color-mix(in oklab, var(--brand-bone) 92%, transparent)",
        borderBottom: "1px solid var(--brand-line)",
      }}
    >
      <div className={`${WRAP} flex items-center gap-8 py-4`}>
        <a href="/" className="flex items-center gap-2.5 no-underline" style={{ color: "var(--brand-teal)" }}>
          <LogoMark size={26} />
          <div
            className="font-display font-semibold text-[16px] tracking-[-0.02em]"
            style={{ color: "var(--brand-ink)" }}
          >
            Lead{" "}
            <span className="font-normal" style={{ color: "var(--brand-fog)" }}>
              Conversa
            </span>
          </div>
        </a>
        <div className="hidden md:flex gap-[22px] text-[13px]">
          <NavLink href="#recursos">Recursos</NavLink>
          <NavLink href="#como">Como funciona</NavLink>
          <NavLink href="#clientes">Clientes</NavLink>
          <NavLink href="#precos">Preços</NavLink>
          <NavLink href="#faq">FAQ</NavLink>
        </div>
        <div className="ml-auto flex items-center gap-2.5">
          <button className={btnClassName()} {...btnProps("ghost")} onClick={goLogin}>
            Entrar
          </button>
          <a href="#precos" className={btnClassName()} {...btnProps("primary")}>
            Começar grátis
          </a>
        </div>
      </div>
    </div>
  );
}

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="no-underline transition-opacity hover:opacity-100"
      style={{ color: "var(--brand-ink)", opacity: 0.8 }}
    >
      {children}
    </a>
  );
}

// ─── HERO ─────────────────────────────────────────────────────────────

function Hero({ goLogin }: { goLogin: () => void }) {
  return (
    <div className={WRAP}>
      <section className="pt-16 pb-14 grid grid-cols-1 lg:grid-cols-[1.08fr_1fr] gap-14 items-center relative">
        <div>
          <Pill>
            <Dot />
            Nova versão · WhatsApp Business API
          </Pill>
          <h1 className="font-serif font-medium text-[42px] sm:text-[52px] lg:text-[64px] leading-[1.02] tracking-[-0.03em] mt-6">
            Cada{" "}
            <em className="italic" style={{ color: "var(--brand-teal)" }}>
              conversa
            </em>{" "}
            é um lead
            <br />
            esperando{" "}
            <em className="italic" style={{ color: "var(--brand-accent-dark)" }}>
              resposta
            </em>
            .
          </h1>
          <p
            className="text-[17px] md:text-[18px] leading-[1.55] max-w-[56ch] mt-6"
            style={{ color: "var(--brand-fog)" }}
          >
            Lead Conversa centraliza WhatsApp, e-mail e automação num lugar só. Converse com
            seus leads nos canais que eles usam, qualifique em escala e converta sem planilha,
            sem ferramenta dupla, sem lead perdido.
          </p>
          <div className="flex gap-3 mt-8 flex-wrap">
            <button className={btnClassName(true)} {...btnProps("primary")} onClick={goLogin}>
              Começar grátis
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <a href="#como" className={btnClassName(true)} {...btnProps("outline")}>
              <Play className="h-3.5 w-3.5" />
              Ver demo · 2 min
            </a>
          </div>
          <div
            className="flex items-center gap-5 mt-7 text-[12px] flex-wrap"
            style={{ color: "var(--brand-fog)" }}
          >
            <div className="flex">
              <Av bg="var(--brand-teal)">M</Av>
              <Av bg="var(--brand-coral)">A</Av>
              <Av bg="var(--brand-violet)">J</Av>
              <Av bg="var(--brand-accent)" color="var(--brand-teal-deep)">
                +
              </Av>
            </div>
            <span>3.400+ empresas conversando</span>
            <span style={{ width: 1, height: 14, background: "var(--brand-line)" }} />
            <span>★ 4.9 / 5 · avaliação G2</span>
          </div>
        </div>

        <HeroProductPeek />
      </section>
    </div>
  );
}

function HeroProductPeek() {
  return (
    <div className="relative h-[480px] lg:h-[520px]">
      {/* Floating badges */}
      <div
        className="absolute flex items-center gap-2.5 px-3.5 py-2.5 rounded-[10px] z-[2]"
        style={{
          top: -14,
          left: -22,
          transform: "rotate(-2deg)",
          background: "var(--card)",
          border: "1px solid var(--brand-line)",
          boxShadow: "0 10px 30px -10px rgba(20,40,60,0.18)",
        }}
      >
        <div
          className="h-7 w-7 rounded-[7px] flex items-center justify-center flex-none"
          style={{ background: "var(--brand-accent-soft)", color: "var(--brand-accent-dark)" }}
        >
          <Send className="h-3.5 w-3.5" />
        </div>
        <div>
          <div className="font-display font-semibold text-[16px] tracking-[-0.02em] leading-none">
            4 min
          </div>
          <div
            className="font-mono text-[10px] tracking-[0.08em] uppercase mt-0.5"
            style={{ color: "var(--brand-fog)" }}
          >
            Resposta média
          </div>
        </div>
      </div>

      <div
        className="absolute flex items-center gap-2.5 px-3.5 py-2.5 rounded-[10px] z-[2]"
        style={{
          bottom: -10,
          right: -16,
          transform: "rotate(1.5deg)",
          background: "var(--card)",
          border: "1px solid var(--brand-line)",
          boxShadow: "0 10px 30px -10px rgba(20,40,60,0.18)",
        }}
      >
        <div
          className="h-7 w-7 rounded-[7px] flex items-center justify-center flex-none"
          style={{
            background: "color-mix(in oklab, var(--brand-teal) 10%, transparent)",
            color: "var(--brand-teal)",
          }}
        >
          <TrendingUp className="h-3.5 w-3.5" />
        </div>
        <div>
          <div className="font-display font-semibold text-[16px] tracking-[-0.02em] leading-none">
            +68%
          </div>
          <div
            className="font-mono text-[10px] tracking-[0.08em] uppercase mt-0.5"
            style={{ color: "var(--brand-fog)" }}
          >
            Taxa resposta
          </div>
        </div>
      </div>

      {/* Product card */}
      <div
        className="absolute inset-0 grid grid-cols-[72px_1fr] overflow-hidden"
        style={{
          background: "var(--card)",
          border: "1px solid var(--brand-line)",
          borderRadius: 14,
          boxShadow:
            "0 30px 60px -30px rgba(20,40,60,0.25), 0 8px 24px -10px rgba(20,40,60,0.12)",
          transform: "rotate(-0.5deg)",
        }}
      >
        <div
          className="py-4 px-2.5 flex flex-col items-center gap-3.5"
          style={{ background: "var(--brand-teal)" }}
        >
          <LogoMark size={20} primary="var(--brand-bone)" accent="var(--brand-accent)" />
          <SideDot active icon={<MessageCircle className="h-3 w-3" />} />
          <SideDot icon={<Users className="h-3 w-3" />} />
          <SideDot icon={<Zap className="h-3 w-3" />} />
          <SideDot icon={<BarChart3 className="h-3 w-3" />} />
        </div>
        <div className="flex flex-col min-w-0">
          <div
            className="flex justify-between items-center px-5 py-3.5"
            style={{ borderBottom: "1px solid var(--brand-line)" }}
          >
            <div className="font-display font-semibold text-[14px] tracking-[-0.015em]">
              Inbox · 24 conversas
            </div>
            <div
              className="font-mono text-[10px] tracking-[0.08em] uppercase flex items-center gap-1.5"
              style={{ color: "var(--brand-fog)" }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: "var(--brand-accent)" }}
              />
              3 ativas agora
            </div>
          </div>
          <div className="grid grid-cols-[1.1fr_1.4fr] flex-1 min-h-0">
            <div
              className="p-2 flex flex-col gap-1 overflow-hidden"
              style={{ borderRight: "1px solid var(--brand-line)" }}
            >
              <InboxItem on av="J" name="Joana M." time="agora" msg="Quero saber do plano anual" unread />
              <InboxItem av="H" avBg="var(--brand-coral)" name="Henrique S." time="2 min" msg="Podemos marcar amanhã 14h?" />
              <InboxItem av="P" avBg="var(--brand-violet)" name="Paula R." time="8 min" msg="Recebi a proposta, obrigada!" />
              <InboxItem av="R" avBg="var(--brand-accent)" avColor="var(--brand-teal-deep)" name="Rafael C." time="14 min" msg="Tem integração com HubSpot?" />
            </div>
            <div className="flex flex-col p-5 gap-2.5 min-w-0">
              <Bubble>Oi! Vi o anúncio de vocês no Instagram.</Bubble>
              <Bubble>Quero saber mais sobre o plano anual, tem desconto?</Bubble>
              <Bubble variant="accent">Automação disparada · qualificando lead</Bubble>
              <Bubble variant="out">Oi Joana 👋 Tem sim — 2 meses grátis no anual. Posso enviar a proposta?</Bubble>
              <Bubble>Perfeito, envia por favor</Bubble>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SideDot({ icon, active }: { icon: React.ReactNode; active?: boolean }) {
  return (
    <div
      className="h-7 w-7 rounded-[7px] flex items-center justify-center"
      style={
        active
          ? { background: "var(--brand-accent)", color: "var(--brand-teal-deep)" }
          : { background: "rgba(255,255,255,0.12)", color: "var(--brand-bone)" }
      }
    >
      {icon}
    </div>
  );
}

function InboxItem({
  on,
  av,
  avBg = "var(--brand-teal)",
  avColor = "var(--brand-bone)",
  name,
  time,
  msg,
  unread,
}: {
  on?: boolean;
  av: string;
  avBg?: string;
  avColor?: string;
  name: string;
  time: string;
  msg: string;
  unread?: boolean;
}) {
  return (
    <div
      className="p-2.5 rounded-[7px] flex gap-2.5 items-start"
      style={on ? { background: "var(--brand-mist)" } : {}}
    >
      <div
        className="h-8 w-8 rounded-full flex items-center justify-center font-display font-semibold text-[12px] flex-none"
        style={{ background: avBg, color: avColor }}
      >
        {av}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[12px] flex justify-between items-center">
          {name}{" "}
          <span
            className="font-mono text-[9px] font-normal"
            style={{ color: "var(--brand-fog)" }}
          >
            {time}
          </span>
        </div>
        <div
          className="text-[11px] truncate mt-0.5"
          style={{ color: "var(--brand-fog)" }}
        >
          {msg}
        </div>
      </div>
      {unread && (
        <div
          className="h-[7px] w-[7px] rounded-full mt-2.5 flex-none"
          style={{ background: "var(--brand-accent)" }}
        />
      )}
    </div>
  );
}

function Bubble({
  children,
  variant = "in",
}: {
  children: React.ReactNode;
  variant?: "in" | "out" | "accent";
}) {
  const base = "max-w-[78%] py-2 px-3 text-[12px] leading-[1.45] rounded-xl";
  if (variant === "out") {
    return (
      <div
        className={`${base} self-end`}
        style={{
          background: "var(--brand-teal)",
          color: "var(--brand-bone)",
          borderBottomRightRadius: 3,
        }}
      >
        {children}
      </div>
    );
  }
  if (variant === "accent") {
    return (
      <div
        className={`${base} self-start font-mono text-[10px] tracking-[0.05em] uppercase border border-dashed`}
        style={{
          background: "var(--brand-accent-soft)",
          color: "var(--brand-accent-dark)",
          borderColor: "color-mix(in oklab, var(--brand-accent) 40%, transparent)",
        }}
      >
        {children}
      </div>
    );
  }
  return (
    <div
      className={`${base} self-start`}
      style={{
        background: "var(--brand-mist)",
        color: "var(--brand-ink)",
        borderBottomLeftRadius: 3,
      }}
    >
      {children}
    </div>
  );
}

// ─── LOGOS ────────────────────────────────────────────────────────────

function Logos() {
  return (
    <div className={WRAP}>
      <div
        className="py-10"
        style={{ borderTop: "1px solid var(--brand-line)", borderBottom: "1px solid var(--brand-line)" }}
      >
        <div
          className="font-mono text-[10px] tracking-[0.2em] uppercase text-center mb-5"
          style={{ color: "var(--brand-fog)" }}
        >
          Mais de 3.400 empresas conversando melhor
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-6 items-center">
          <FakeLogo>Paradigma</FakeLogo>
          <FakeLogo font="serif">Matera</FakeLogo>
          <FakeLogo font="mono">CASA 9</FakeLogo>
          <FakeLogo>Dolce Vita</FakeLogo>
          <FakeLogo font="serif">Tramontano</FakeLogo>
          <FakeLogo font="mono">NORD/SUL</FakeLogo>
        </div>
      </div>
    </div>
  );
}

function FakeLogo({
  children,
  font = "sans",
}: {
  children: React.ReactNode;
  font?: "sans" | "serif" | "mono";
}) {
  const fontCls =
    font === "serif"
      ? "font-serif italic font-medium text-[17px]"
      : font === "mono"
        ? "font-mono text-[13px] tracking-[0.1em] uppercase"
        : "font-display font-semibold text-[17px] tracking-[-0.02em]";
  return (
    <div
      className={`text-center opacity-80 ${fontCls}`}
      style={{ color: "var(--brand-fog)" }}
    >
      {children}
    </div>
  );
}

// ─── FEATURES ─────────────────────────────────────────────────────────

function Features() {
  return (
    <section
      id="recursos"
      className="py-20"
      style={{
        background: "var(--card)",
        borderTop: "1px solid var(--brand-line)",
        borderBottom: "1px solid var(--brand-line)",
      }}
    >
      <div className={WRAP}>
        <SecHead
          eyebrow="Recursos"
          title={
            <>
              Tudo que sua equipe precisa para{" "}
              <em className="italic" style={{ color: "var(--brand-accent-dark)" }}>
                conversar melhor
              </em>
              .
            </>
          }
          sub="Uma plataforma que substitui planilha, CRM básico e ferramentas avulsas. Feita para times que vendem pela conversa."
        />
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px overflow-hidden rounded-[14px]"
          style={{ background: "var(--brand-line)", border: "1px solid var(--brand-line)" }}
        >
          <Feat
            tone="teal"
            icon={<MessageCircle className="h-5 w-5" />}
            title="Caixa de entrada unificada"
            desc="WhatsApp, e-mail e formulários num só inbox. Toda a equipe vendo a mesma conversa, sem trombada."
          />
          <Feat
            tone="accent"
            icon={<Zap className="h-5 w-5" />}
            title="Automação de conversa"
            desc="Fluxos condicionais para qualificar, agendar, nutrir e reengajar. Sem código. Ativa pelo canal que o lead usa."
          />
          <Feat
            tone="coral"
            icon={<Send className="h-5 w-5" />}
            title="Campanhas em escala"
            desc="Envios segmentados por interesse, origem e comportamento. Templates aprovados no WhatsApp oficial."
          />
          <Feat
            tone="violet"
            icon={<UserPlus className="h-5 w-5" />}
            title="CRM de leads"
            desc="Cada contato com histórico, tags, notas e pipeline visual. Qualificação automática por score."
          />
          <Feat
            tone="amber"
            icon={<TrendingUp className="h-5 w-5" />}
            title="Relatórios em tempo real"
            desc="Taxa de resposta, conversão, tempo médio, ranking de atendentes. Com filtros que funcionam."
          />
          <Feat
            tone="teal-deep"
            icon={<Grid3x3 className="h-5 w-5" />}
            title="Integrações nativas"
            desc="HubSpot, Pipedrive, RD Station, Google Sheets e webhooks. Sincroniza em ambas direções."
          />
        </div>
      </div>
    </section>
  );
}

function Feat({
  tone,
  icon,
  title,
  desc,
}: {
  tone: "teal" | "accent" | "coral" | "violet" | "amber" | "teal-deep";
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  const map: Record<string, { bg: string; fg: string }> = {
    teal: {
      bg: "color-mix(in oklab, var(--brand-teal) 10%, transparent)",
      fg: "var(--brand-teal)",
    },
    accent: { bg: "var(--brand-accent-soft)", fg: "var(--brand-accent-dark)" },
    coral: {
      bg: "color-mix(in oklab, var(--brand-coral) 14%, transparent)",
      fg: "var(--brand-coral)",
    },
    violet: {
      bg: "color-mix(in oklab, var(--brand-violet) 14%, transparent)",
      fg: "var(--brand-violet)",
    },
    amber: {
      bg: "color-mix(in oklab, var(--brand-amber) 14%, transparent)",
      fg: "color-mix(in oklab, var(--brand-amber) 70%, black)",
    },
    "teal-deep": {
      bg: "color-mix(in oklab, var(--brand-teal) 10%, transparent)",
      fg: "var(--brand-teal-deep)",
    },
  };
  const s = map[tone];
  return (
    <div
      className="px-[30px] py-[34px] flex flex-col gap-3.5"
      style={{ background: "var(--card)" }}
    >
      <div
        className="h-11 w-11 rounded-[10px] flex items-center justify-center flex-none"
        style={{ background: s.bg, color: s.fg }}
      >
        {icon}
      </div>
      <h3 className="font-display font-semibold text-[19px] tracking-[-0.02em] m-0 mt-1.5">
        {title}
      </h3>
      <p
        className="text-[14px] leading-[1.55] m-0"
        style={{ color: "var(--brand-fog)" }}
      >
        {desc}
      </p>
    </div>
  );
}

// ─── FLOW ─────────────────────────────────────────────────────────────

function Flow() {
  return (
    <section id="como" className="py-20">
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-center`}>
        <div>
          <Eyebrow>Como funciona</Eyebrow>
          <h2 className="font-serif font-medium text-[32px] md:text-[40px] leading-[1.1] tracking-[-0.025em] mt-3 mb-4">
            Do{" "}
            <em className="italic" style={{ color: "var(--brand-teal)" }}>
              primeiro oi
            </em>
            <br />
            ao cliente fechado.
          </h2>
          <p className="text-[16px] leading-[1.55] mb-6" style={{ color: "var(--brand-fog)" }}>
            Toda conversa começa num canal. Todo lead precisa de resposta. A Lead Conversa
            conecta os dois com automação no meio — sem que pareça robô.
          </p>
          <ul className="list-none p-0 m-0 flex flex-col gap-3.5">
            <FlowStep n={1} title="Lead chega pelo canal.">
              WhatsApp, Instagram, e-mail, formulário do site. Todos caem no mesmo inbox.
            </FlowStep>
            <FlowStep n={2} title="Automação qualifica.">
              Pergunta, segmenta, atribui. Só leads prontos chegam na equipe humana.
            </FlowStep>
            <FlowStep n={3} title="Atendente converte.">
              Com histórico, templates e respostas prontas. Tudo fica no CRM automaticamente.
            </FlowStep>
            <FlowStep n={4} title="Relatório mostra o que funciona.">
              Ajusta fluxo, templates e campanhas com base em dado real, não achismo.
            </FlowStep>
          </ul>
        </div>

        <div
          className="rounded-[14px] p-[30px] grid grid-rows-[auto_1fr] gap-4.5"
          style={{
            background: "var(--card)",
            border: "1px solid var(--brand-line)",
            boxShadow: "0 20px 40px -20px rgba(20,40,60,0.1)",
          }}
        >
          <div
            className="flex justify-between items-center font-mono text-[10px] tracking-[0.1em] uppercase"
            style={{ color: "var(--brand-fog)" }}
          >
            <span>Fluxo automatizado</span>
            <span>tempo real</span>
          </div>
          <div className="flex flex-col gap-3 mt-2">
            <FlowVizStep
              tone="wa"
              icon={<MessageCircle className="h-4 w-4" />}
              title="WhatsApp recebe nova mensagem"
              sub='Joana: "Quero saber do plano anual"'
              arrow
            />
            <FlowVizStep
              tone="accent"
              icon={<Zap className="h-4 w-4" />}
              title="Automação identifica intenção"
              sub={`Tag: "interesse-anual" · score +20`}
              arrow
            />
            <FlowVizStep
              tone="coral"
              icon={<Users className="h-4 w-4" />}
              title="Atribuído para atendente"
              sub="Mariana · squad comercial"
              arrow
            />
            <FlowVizStep
              tone="teal"
              icon={<CheckCircle2 className="h-4 w-4" />}
              title="Proposta enviada · deal aberto"
              sub="R$ 4.800/ano · pipeline atualizado"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function FlowStep({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-3.5 items-start">
      <span
        className="h-7 w-7 rounded-full flex items-center justify-center font-display font-semibold text-[12px] flex-none"
        style={{ background: "var(--brand-accent-soft)", color: "var(--brand-accent-dark)" }}
      >
        {n}
      </span>
      <div>
        <strong className="font-display font-semibold tracking-[-0.015em]">{title}</strong>
        <br />
        <span className="text-[14px]" style={{ color: "var(--brand-fog)" }}>
          {children}
        </span>
      </div>
    </li>
  );
}

function FlowVizStep({
  tone,
  icon,
  title,
  sub,
  arrow,
}: {
  tone: "wa" | "accent" | "coral" | "teal";
  icon: React.ReactNode;
  title: string;
  sub: string;
  arrow?: boolean;
}) {
  const map: Record<string, { bg: string; fg: string }> = {
    wa: { bg: "#25D36611", fg: "#1aaa4f" },
    accent: { bg: "var(--brand-accent-soft)", fg: "var(--brand-accent-dark)" },
    coral: {
      bg: "color-mix(in oklab, var(--brand-coral) 14%, transparent)",
      fg: "var(--brand-coral)",
    },
    teal: {
      bg: "color-mix(in oklab, var(--brand-teal) 10%, transparent)",
      fg: "var(--brand-teal)",
    },
  };
  const s = map[tone];
  return (
    <div
      className="flex items-center gap-3 px-4 py-3.5 rounded-[10px]"
      style={{ border: "1px solid var(--brand-line)", background: "var(--brand-bone)" }}
    >
      <div
        className="h-8 w-8 rounded-[8px] flex items-center justify-center flex-none"
        style={{ background: s.bg, color: s.fg }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-medium text-[13px] tracking-[-0.01em]">
          {title}
        </div>
        <div className="text-[11px] mt-0.5" style={{ color: "var(--brand-fog)" }}>
          {sub}
        </div>
      </div>
      {arrow && (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={{ color: "var(--brand-fog)" }}
        >
          <path d="M12 5v14M19 12l-7 7-7-7" />
        </svg>
      )}
    </div>
  );
}

// ─── STATS BAND ───────────────────────────────────────────────────────

function StatsBand() {
  return (
    <div className={WRAP}>
      <div
        className="rounded-[20px] p-10 md:p-[50px] grid grid-cols-1 md:grid-cols-[1.2fr_2fr] gap-10 items-center relative overflow-hidden"
        style={{ background: "var(--brand-teal)", color: "var(--brand-bone)" }}
      >
        <div
          className="absolute pointer-events-none opacity-[0.08]"
          style={{ right: -160, top: -160 }}
        >
          <LogoMark size={400} primary="var(--brand-bone)" accent="var(--brand-accent)" />
        </div>
        <h3 className="font-serif text-[28px] md:text-[34px] leading-[1.1] tracking-[-0.02em] m-0 font-medium relative">
          Times que usam Lead Conversa{" "}
          <em className="italic" style={{ color: "var(--brand-accent)" }}>
            respondem primeiro
          </em>
          .
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-7 relative">
          <StatItem n="4 min" l="Resposta média" desc="Antes, 2 horas. Depois da automação de triagem." />
          <StatItem n="+68%" l="Taxa resposta" desc="Cresceu ao centralizar os canais num só inbox." />
          <StatItem n="3.400" l="Empresas ativas" desc="De e-commerce a educação a serviços B2B." />
        </div>
      </div>
    </div>
  );
}

function StatItem({ n, l, desc }: { n: string; l: string; desc: string }) {
  return (
    <div>
      <div className="font-display font-semibold text-[44px] md:text-[48px] tracking-[-0.03em] leading-none">
        {n}
      </div>
      <div className="font-mono text-[12px] tracking-[0.1em] uppercase mt-1.5 opacity-70">{l}</div>
      <div
        className="text-[13px] mt-2.5 leading-[1.5]"
        style={{ color: "rgba(250,250,247,0.75)" }}
      >
        {desc}
      </div>
    </div>
  );
}

// ─── TESTIMONIAL ──────────────────────────────────────────────────────

function Testimonial() {
  return (
    <section id="clientes" className="py-20">
      <div className={WRAP}>
        <div className="max-w-[860px] mx-auto text-center">
          <div className="mb-5">
            <Eyebrow>Quem usa</Eyebrow>
          </div>
          <div className="font-serif font-medium text-[24px] md:text-[32px] leading-[1.25] tracking-[-0.02em]">
            <span
              className="font-serif mr-1"
              style={{ color: "var(--brand-accent)", fontSize: "1.6em", lineHeight: 0, verticalAlign: "-0.3em" }}
            >
              “
            </span>
            Trocamos planilha e três ferramentas por uma só. Nosso tempo de primeira resposta
            caiu de 2h para 4 minutos. E o pessoal do comercial parou de reclamar do CRM.
          </div>
          <div className="mt-7 flex justify-center items-center gap-3">
            <div
              className="h-[42px] w-[42px] rounded-full flex items-center justify-center font-display font-semibold text-[15px]"
              style={{ background: "var(--brand-teal)", color: "var(--brand-bone)" }}
            >
              MR
            </div>
            <div className="text-left">
              <div className="font-display font-semibold text-[14px] tracking-[-0.015em]">
                Mariana Ribeiro
              </div>
              <div
                className="font-mono text-[11px] tracking-[0.08em] uppercase"
                style={{ color: "var(--brand-fog)" }}
              >
                Head de Growth · Paradigma
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── PRICING ──────────────────────────────────────────────────────────

function Pricing({ goLogin }: { goLogin: () => void }) {
  return (
    <section id="precos" className="py-20">
      <div className={WRAP}>
        <SecHead
          eyebrow="Preços"
          title={
            <>
              Comece grátis. Cresça{" "}
              <em className="italic" style={{ color: "var(--brand-accent-dark)" }}>
                quando quiser
              </em>
              .
            </>
          }
          sub="Sem fidelidade. Sem taxa de setup. Upgrade ou downgrade a qualquer momento."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          <Plan
            name="Free"
            desc="Para experimentar a plataforma sem compromisso."
            price="Grátis"
            features={[
              "500 contatos",
              "1.000 mensagens/mês",
              "2 usuários",
              "5 fluxos de automação",
            ]}
            cta="Começar grátis"
            onClick={goLogin}
          />
          <Plan
            name="Starter"
            desc="Para times pequenos começando a escalar."
            price="R$ 97"
            unit="/mês"
            features={[
              "5.000 contatos",
              "10.000 mensagens/mês",
              "5 usuários",
              "20 fluxos de automação",
            ]}
            cta="Assinar Starter"
            onClick={goLogin}
          />
          <Plan
            featured
            tag="Mais popular"
            name="Pro"
            desc="Para operações que rodam volume de verdade."
            price="R$ 297"
            unit="/mês"
            features={[
              "25.000 contatos",
              "50.000 mensagens/mês",
              "15 usuários",
              "100 fluxos de automação",
            ]}
            cta="Assinar Pro"
            onClick={goLogin}
          />
          <Plan
            name="Enterprise"
            desc="Para empresas com alto volume e SLA."
            price="Sob consulta"
            features={[
              "Contatos ilimitados",
              "Mensagens sob demanda",
              "Usuários ilimitados",
              "Fluxos ilimitados",
              "SLA + onboarding dedicado",
            ]}
            cta="Falar com vendas"
            onClick={goLogin}
          />
        </div>
      </div>
    </section>
  );
}

function Plan({
  name,
  desc,
  price,
  unit,
  features,
  cta,
  featured,
  tag,
  onClick,
}: {
  name: string;
  desc: string;
  price: string;
  unit?: string;
  features: string[];
  cta: string;
  featured?: boolean;
  tag?: string;
  onClick: () => void;
}) {
  const shell = featured
    ? {
        background: "var(--brand-teal)",
        color: "var(--brand-bone)",
        border: "1px solid var(--brand-teal)",
        boxShadow:
          "0 24px 50px -20px color-mix(in oklab, var(--brand-teal) 50%, transparent)",
      }
    : {
        background: "var(--card)",
        border: "1px solid var(--brand-line)",
      };
  return (
    <div
      className="rounded-[14px] px-7 py-8 flex flex-col gap-[18px] relative"
      style={shell}
    >
      {tag && (
        <div
          className="absolute top-[-11px] right-6 font-mono text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 rounded-md font-semibold"
          style={{ background: "var(--brand-accent)", color: "var(--brand-teal-deep)" }}
        >
          {tag}
        </div>
      )}
      <div>
        <div className="font-display font-semibold text-[18px] tracking-[-0.02em]">
          {name}
        </div>
        <div
          className="text-[13px]"
          style={{ color: featured ? "var(--brand-bone)" : "var(--brand-fog)", opacity: featured ? 0.75 : 1 }}
        >
          {desc}
        </div>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-display font-semibold text-[40px] md:text-[46px] tracking-[-0.03em] leading-none">
          {price}
        </span>
        {unit && (
          <span
            className="text-[13px]"
            style={{
              color: featured ? "rgba(250,250,247,0.6)" : "var(--brand-fog)",
            }}
          >
            {unit}
          </span>
        )}
      </div>
      <ul className="list-none m-0 p-0 flex flex-col gap-2.5 text-[13px]">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check
              className="h-3.5 w-3.5 mt-[2px] flex-none"
              style={{ color: featured ? "var(--brand-accent)" : "var(--brand-accent-dark)" }}
            />
            <span style={{ color: featured ? "var(--brand-bone)" : "var(--brand-ink)" }}>
              {f}
            </span>
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        className={btnClassName()}
        {...btnProps(featured ? "accent" : "outline")}
      >
        {cta}
      </button>
    </div>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────

function FAQ() {
  const qs = [
    {
      q: "Preciso do WhatsApp Business API para usar?",
      a: "No plano Starter você pode começar com WhatsApp comum. Para campanhas, automação e múltiplos atendentes num mesmo número, usamos a API oficial da Meta — a gente cuida de todo o cadastro pra você.",
    },
    {
      q: "Tem fidelidade ou taxa de setup?",
      a: "Nenhuma. Mensal, sem fidelidade, cancela quando quiser pelo painel. Setup e onboarding estão incluídos em todos os planos pagos.",
    },
    {
      q: "Funciona com meu CRM atual?",
      a: "Temos integração nativa com HubSpot, Pipedrive, RD Station e Google Sheets. Para os demais, webhooks e Zapier cobrem 99% dos casos. Time técnico disponível pra ajudar.",
    },
    {
      q: "Quanto tempo leva pra começar?",
      a: "No Starter, 10 minutos. No Business, de 1 a 3 dias úteis — a parte lenta é a aprovação da Meta, que a gente acompanha junto com você.",
    },
    {
      q: "Os dados dos meus clientes ficam seguros?",
      a: "Servidores no Brasil, criptografia em trânsito e repouso, LGPD compliance total. Cada operador vê apenas o que tem permissão. Auditoria de acessos inclusa em todos os planos.",
    },
  ];
  return (
    <section id="faq" className="py-20">
      <div className={WRAP}>
        <SecHead
          eyebrow="FAQ"
          title={
            <>
              Dúvidas{" "}
              <em className="italic" style={{ color: "var(--brand-accent-dark)" }}>
                frequentes
              </em>
              .
            </>
          }
        />
        <div className="max-w-[760px] mx-auto flex flex-col gap-1">
          {qs.map((item, i) => (
            <details
              key={item.q}
              className="rounded-[10px] overflow-hidden group"
              style={{ background: "var(--card)", border: "1px solid var(--brand-line)" }}
              open={i === 0}
            >
              <summary
                className="px-5 py-4 cursor-pointer flex justify-between items-center font-display font-medium text-[15px] tracking-[-0.015em]"
                style={{ listStyle: "none" }}
              >
                <span>{item.q}</span>
                <span
                  className="font-display text-[22px] font-normal leading-none transition-transform group-open:rotate-180"
                  style={{ color: "var(--brand-fog)" }}
                >
                  +
                </span>
              </summary>
              <div
                className="px-5 pb-5 text-[14px] leading-[1.6]"
                style={{ color: "var(--brand-fog)" }}
              >
                {item.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FINAL CTA ────────────────────────────────────────────────────────

function FinalCTA({ goLogin }: { goLogin: () => void }) {
  return (
    <div className={WRAP}>
      <div
        className="rounded-[20px] px-6 md:px-12 py-14 md:py-16 text-center relative overflow-hidden"
        style={{ background: "var(--brand-teal)", color: "var(--brand-bone)" }}
      >
        <div
          className="absolute pointer-events-none opacity-[0.10]"
          style={{ left: -120, bottom: -120 }}
        >
          <LogoMark size={420} primary="var(--brand-bone)" accent="var(--brand-accent)" />
        </div>
        <h2 className="font-serif font-medium text-[32px] md:text-[46px] leading-[1.08] tracking-[-0.025em] m-0 mb-3.5 relative">
          Pronto pra responder{" "}
          <em className="italic" style={{ color: "var(--brand-accent)" }}>
            em 4 minutos
          </em>
          ?
        </h2>
        <p
          className="text-[15px] md:text-[17px] mx-auto max-w-[54ch] mb-7 relative"
          style={{ color: "rgba(250,250,247,0.75)" }}
        >
          Comece grátis hoje. Sem cartão de crédito, sem fidelidade, sem compromisso.
        </p>
        <div className="flex gap-3 justify-center flex-wrap relative">
          <button className={btnClassName(true)} {...btnProps("accent")} onClick={goLogin}>
            Começar grátis
          </button>
          <a
            href="#"
            className={btnClassName(true)}
            {...btnProps("outline", { color: "var(--brand-bone)", borderColor: "rgba(250,250,247,0.3)" })}
          >
            Agendar demo
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── FOOTER ───────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="pt-16 pb-7 mt-20" style={{ borderTop: "1px solid var(--brand-line)" }}>
      <div className={WRAP}>
        <div className="grid grid-cols-2 md:grid-cols-[1.4fr_repeat(3,1fr)] gap-10">
          <div>
            <a href="/" className="flex items-center gap-2.5 no-underline" style={{ color: "var(--brand-teal)" }}>
              <LogoMark size={24} />
              <span
                className="font-display font-semibold text-[16px] tracking-[-0.02em]"
                style={{ color: "var(--brand-ink)" }}
              >
                Lead{" "}
                <span className="font-normal" style={{ color: "var(--brand-fog)" }}>
                  Conversa
                </span>
              </span>
            </a>
            <p
              className="text-[13px] leading-[1.55] mt-4 max-w-[32ch]"
              style={{ color: "var(--brand-fog)" }}
            >
              Plataforma de conversação e marketing para times que vendem pela conversa.
            </p>
          </div>
          <FootCol
            title="Produto"
            items={[
              { label: "Recursos", href: "#recursos" },
              { label: "Integrações", href: "#recursos" },
              { label: "Preços", href: "#precos" },
              { label: "Changelog", href: "#" },
            ]}
          />
          <FootCol
            title="Empresa"
            items={[
              { label: "Sobre", href: "#" },
              { label: "Blog", href: "#" },
              { label: "Clientes", href: "#clientes" },
              { label: "Carreiras", href: "#" },
            ]}
          />
          <FootCol
            title="Legal"
            items={[
              { label: "Termos de uso", href: "/termos" },
              { label: "Privacidade", href: "/privacidade" },
              { label: "LGPD", href: "/lgpd" },
              { label: "Status", href: "#" },
            ]}
          />
        </div>
        <div
          className="mt-11 pt-5 flex justify-between flex-wrap gap-2 font-mono text-[11px] tracking-[0.08em]"
          style={{ borderTop: "1px solid var(--brand-line)", color: "var(--brand-fog)" }}
        >
          <span>© {new Date().getFullYear()} Lead Conversa · Todos os direitos reservados</span>
          <span>Feito no Brasil</span>
        </div>
      </div>
    </footer>
  );
}

function FootCol({
  title,
  items,
}: {
  title: string;
  items: { label: string; href: string }[];
}) {
  return (
    <div>
      <h4 className="font-display font-semibold text-[12px] tracking-[0.06em] uppercase m-0 mb-3.5">
        {title}
      </h4>
      {items.map((it) => (
        <a
          key={it.label}
          href={it.href}
          className="block no-underline text-[13px] py-1"
          style={{ color: "var(--brand-fog)" }}
        >
          {it.label}
        </a>
      ))}
    </div>
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────

function SecHead({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string;
  title: React.ReactNode;
  sub?: string;
}) {
  return (
    <div className="max-w-[720px] mx-auto text-center mb-12">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="font-serif font-medium text-[32px] md:text-[44px] leading-[1.08] tracking-[-0.025em] mt-3.5 mb-4">
        {title}
      </h2>
      {sub && (
        <div className="text-[16px] md:text-[17px] leading-[1.55]" style={{ color: "var(--brand-fog)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="font-mono text-[10px] tracking-[0.22em] uppercase"
      style={{ color: "var(--brand-teal)" }}
    >
      {children}
    </div>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-mono text-[10px] tracking-[0.14em] uppercase border"
      style={{
        background: "var(--brand-mist)",
        color: "var(--brand-teal)",
        borderColor: "var(--brand-line)",
      }}
    >
      {children}
    </span>
  );
}

function Dot() {
  return (
    <span
      className="h-1.5 w-1.5 rounded-full"
      style={{
        background: "var(--brand-accent)",
        boxShadow: "0 0 0 3px color-mix(in oklab, var(--brand-accent) 30%, transparent)",
      }}
    />
  );
}

function Av({
  children,
  bg,
  color = "var(--brand-bone)",
}: {
  children: React.ReactNode;
  bg: string;
  color?: string;
}) {
  return (
    <div
      className="h-7 w-7 rounded-full flex items-center justify-center font-display text-[10px] font-semibold -ml-2 first:ml-0"
      style={{
        background: bg,
        color,
        border: "2px solid var(--brand-bone)",
      }}
    >
      {children}
    </div>
  );
}

// ─── Button class name constants ─────────────────────────────────────

const btnBase =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg font-display font-medium text-[13px] tracking-[-0.01em] cursor-pointer no-underline transition-colors border border-transparent";
const btnLg = "px-5 py-3.5 text-[14px]";

// Variant → [base, hover] CSS properties (colors applied inline so we can
// handle hover without extra CSS).
const VARIANT: Record<
  "primary" | "accent" | "ghost" | "outline",
  [React.CSSProperties, React.CSSProperties]
> = {
  primary: [
    { background: "var(--brand-teal)", color: "var(--brand-bone)" },
    { background: "var(--brand-teal-deep)", color: "var(--brand-bone)" },
  ],
  accent: [
    { background: "var(--brand-accent)", color: "var(--brand-ink)" },
    { background: "var(--brand-accent-dark)", color: "var(--brand-bone)" },
  ],
  ghost: [
    { background: "transparent", color: "var(--brand-ink)" },
    { background: "transparent", color: "var(--brand-teal)" },
  ],
  outline: [
    {
      background: "transparent",
      color: "var(--brand-ink)",
      borderColor: "var(--brand-line)",
    },
    {
      background: "var(--brand-mist)",
      color: "var(--brand-ink)",
      borderColor: "var(--brand-line)",
    },
  ],
};

function btnClassName(lg?: boolean) {
  return `${btnBase} ${lg ? btnLg : ""}`;
}

function btnProps(
  variant: keyof typeof VARIANT,
  extra?: React.CSSProperties,
): Pick<
  React.HTMLAttributes<HTMLElement>,
  "style" | "onMouseEnter" | "onMouseLeave"
> {
  const [base, hover] = VARIANT[variant];
  return {
    style: { ...base, ...extra },
    onMouseEnter: (e) =>
      Object.assign((e.currentTarget as HTMLElement).style, hover, extra),
    onMouseLeave: (e) =>
      Object.assign((e.currentTarget as HTMLElement).style, base, extra),
  };
}
